import {
	agentStatusAllowsPickingUpAndResumingInteraction,
	interactionCanBePickedUp,
} from "@/common/helpers";
import { HandIcon } from "@/common/icons";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { useInteractionBarCommonStyle } from "@/common/style/interactionBarCommonStyle";
import { InteractionPopoverType } from "@/common/types";
import { Flex } from "@/components/atoms/flex";
import { MessagingConversationSummaryButton } from "@/components/atoms/messagingConversationSummary";
import { NotesButton } from "@/components/atoms/notes";
import { SquareToggleButton } from "@/components/atoms/squareToggleButton";
import { useEmailInteractionPreview } from "@/components/organisms/interaction/useEmailInteractionPreview";
import { useInteractionPickUp } from "@/components/organisms/interaction/useInteractionPickUp";
import { useAgentInteractionState } from "@/hooks/agent";
import {
	type QueuesState,
	useQueuesStore,
} from "@/common/stores/nonpersistingStores/queues";
import { InteractionStatus } from "@/repository/models";
import { mergeClasses, tokens } from "@fluentui/react-components";
import { Dismiss20Regular, Play20Regular } from "@fluentui/react-icons";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { unParkTextInteraction } from "@/common/helpers/parkHelper";

export const EmailPreviewActionButtons = () => {
	const style = useInteractionBarCommonStyle();
	const intl = useIntl();

	const [interactionPopoverType, setInteractionPopoverType] =
		useState<InteractionPopoverType | null>(null);

	const {
		agentHasCallInProgressOrTextInOffering,
		agentHasInteractionInOffering,
	} = useAgentInteractionState();

	const previewInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.preview
	);

	const queues = useQueuesStore((state: QueuesState) => state.queues);

	// A previewed interaction is always on one of the agent's own queues, so the queue list carries the
	// master switch (History has to ask the backend instead, because it spans queues the agent is not in).
	const enableSummary =
		queues.find((q) => q.id === previewInteraction?.queueId)
			?.enableTextSummary ?? false;

	const { hideEmailPreview } = useEmailInteractionPreview();

	const pickUpInteraction = useInteractionPickUp();

	const toggleInteractionPopoverOpening = useCallback(
		(type: InteractionPopoverType) => {
			setInteractionPopoverType((oldState) =>
				oldState === type ? null : type
			);
		},
		[setInteractionPopoverType]
	);

	const itemCanBePickedUp = useMemo(() => {
		return (
			previewInteraction &&
			interactionCanBePickedUp(
				{ ...previewInteraction },
				agentHasInteractionInOffering,
				agentHasCallInProgressOrTextInOffering
			)
		);
	}, [
		previewInteraction,
		interactionCanBePickedUp,
		agentHasInteractionInOffering,
		agentHasCallInProgressOrTextInOffering,
	]);

	const itemCanBeResumed = useMemo(() => {
		return (
			previewInteraction &&
			agentStatusAllowsPickingUpAndResumingInteraction(
				{ type: previewInteraction.type },
				agentHasInteractionInOffering,
				agentHasCallInProgressOrTextInOffering
			)
		);
	}, [
		previewInteraction,
		agentStatusAllowsPickingUpAndResumingInteraction,
		agentHasInteractionInOffering,
		agentHasCallInProgressOrTextInOffering,
	]);

	return (
		<Flex
			vAlign="center"
			className={mergeClasses(style.interactionText, style.interactionButtons)}
		>
			<NotesButton
				interactionId={previewInteraction?.id}
				editMode={true}
				toggleInteractionPopoverOpening={toggleInteractionPopoverOpening}
				interactionPopoverType={interactionPopoverType}
			/>
			{previewInteraction && (
				<MessagingConversationSummaryButton
					canGenerateSummary={true}
					summaryFeatureEnabled={enableSummary}
					interactionId={previewInteraction.id}
					interactionType={previewInteraction.type}
					interactionStartDate={previewInteraction.startDate?.toString()}
					firstAgentAssignedAt={previewInteraction.firstAgentAssignedAt}
				/>
			)}
			{previewInteraction?.status == InteractionStatus.Parked && (
				<SquareToggleButton
					icon={<Play20Regular />}
					tooltip={intl.formatMessage({
						id: "Interaction.Buttons.Resume",
					})}
					className={mergeClasses(
						style.squareColoredButton,
						style.greenColor,
						!itemCanBeResumed && style.disabled
					)}
					onClick={(_: any) => {
						if (previewInteraction) {
							unParkTextInteraction(
								previewInteraction.id,
								previewInteraction.type
							);
						}
					}}
					disabled={!itemCanBeResumed}
				/>
			)}
			{previewInteraction &&
				previewInteraction?.status != InteractionStatus.Parked && (
					<SquareToggleButton
						icon={<HandIcon />}
						tooltip={intl.formatMessage({
							id: "Interaction.Buttons.PickUp",
						})}
						className={mergeClasses(
							style.squareColoredButton,
							style.greenColor,
							!itemCanBePickedUp && style.disabled
						)}
						onClick={(_: any) => {
							if (previewInteraction) {
								pickUpInteraction(_, previewInteraction);
							}
						}}
						disabled={!itemCanBePickedUp}
					/>
				)}
			<SquareToggleButton
				icon={
					<Dismiss20Regular
						primaryFill={tokens.colorNeutralForegroundStaticInverted}
					/>
				}
				tooltip={intl.formatMessage({
					id: "Interaction.Buttons.CloseThePreview",
				})}
				className={mergeClasses(style.squareColoredButton, style.redColor)}
				onClick={hideEmailPreview}
			/>
		</Flex>
	);
};
